import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Maximize2,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Plus,
  Check
} from 'lucide-react';
import { apiRequest, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

export default function RemindersWidget({ expandable = true }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ today: [], overdue: [], total_today: 0, total_overdue: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReminders();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchReminders, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await apiRequest('/reminders/today');
      setData(response || { today: [], overdue: [], total_today: 0, total_overdue: 0 });
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (reminderId) => {
    try {
      await apiRequest(`/reminders/${reminderId}/acknowledge`, { method: 'POST' });
      toast.success('Reminder acknowledged');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to acknowledge reminder');
    }
  };

  const allReminders = [...(data.overdue || []), ...(data.today || [])].slice(0, 5);
  const totalCount = (data.total_today || 0) + (data.total_overdue || 0);

  return (
    <Card data-testid="reminders-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-lg">Reminders</CardTitle>
            {totalCount > 0 && (
              <Badge 
                variant={data.total_overdue > 0 ? "destructive" : "secondary"} 
                className="ml-1"
              >
                {totalCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {expandable && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/reminders')}
                className="h-8 w-8"
                title="Expand"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : allReminders.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <CheckCircle className="h-10 w-10 mx-auto mb-2 text-green-300" />
            <p className="text-sm">All caught up!</p>
            <p className="text-xs mt-1 text-slate-400">No pending reminders</p>
            <Button 
              variant="ghost" 
              size="sm" 
              className="mt-2"
              onClick={() => navigate('/reminders')}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Reminder
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {allReminders.map((reminder) => {
              const isOverdue = reminder.is_overdue || 
                (reminder.remind_at && new Date(reminder.remind_at) < new Date());
              const dueDate = new Date(reminder.remind_at);
              
              return (
                <div
                  key={reminder.id}
                  className={`p-3 rounded-lg border flex items-start gap-3 ${
                    isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                  }`}
                >
                  <button
                    onClick={() => handleAcknowledge(reminder.id)}
                    className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                      isOverdue
                        ? 'border-red-400 hover:border-green-500 hover:bg-green-50'
                        : 'border-amber-400 hover:border-green-500 hover:bg-green-50'
                    }`}
                    title="Mark as done"
                  >
                    <Check className="h-3 w-3 text-slate-400 hover:text-green-600" />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-900 truncate">
                      {reminder.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-slate-400" />
                        <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                          {formatDate(dueDate)}
                        </span>
                      </span>
                      {isOverdue && (
                        <Badge variant="destructive" className="text-xs px-1 py-0">
                          <AlertTriangle className="h-3 w-3 mr-0.5" />
                          Overdue
                        </Badge>
                      )}
                      {reminder.linked_task_id && (
                        <Badge variant="outline" className="text-xs px-1 py-0 bg-blue-50">
                          Task
                        </Badge>
                      )}
                      {reminder.linked_todo_id && (
                        <Badge variant="outline" className="text-xs px-1 py-0 bg-purple-50">
                          To-Do
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {totalCount > 5 && (
              <Button 
                variant="link" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => navigate('/reminders')}
              >
                View all {totalCount} reminders
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
